import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronsUpDown, Check, Filter } from "lucide-react";

export interface OptionItem {
  label: string;
  value: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: OptionItem[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

export function MultiSelectFilter({ label, options, values, onChange, placeholder }: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (val: string) => {
    if (values.includes(val)) onChange(values.filter(v => v !== val));
    else onChange([...values, val]);
  };

  const clear = () => onChange([]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="justify-start gap-2">
          <Filter className="h-4 w-4" />
          <span>{label}</span>
          {values.length > 0 && (
            <Badge variant="secondary" className="ml-1">{values.length}</Badge>
          )}
          <ChevronsUpDown className="ml-auto h-4 w-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3">
        <div className="mb-2 text-sm font-medium">{label}</div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder || "Search..."}
          className="mb-2 h-8"
        />
        <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
          {filtered.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/60"
            >
              <Checkbox checked={values.includes(opt.value)} onCheckedChange={() => toggle(opt.value)} />
              <span className="text-sm">{opt.label}</span>
              {values.includes(opt.value) && <Check className="ml-auto h-4 w-4" />}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="text-sm text-muted-foreground py-4 text-center">No results</div>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
          <Button size="sm" onClick={() => setOpen(false)}>Done</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
